import type { ReactNode } from 'react'
import { cn } from '../lib/utils'

interface Props {
  label: string
  value: string
  /** Sub-text rendered next to the value (e.g. "/ 14"). */
  sub?: string
  /** Footer line — short delta or context. */
  hint?: ReactNode
  /** Color modifier for `hint`. */
  hintTone?: 'up' | 'down' | 'neutral'
  className?: string
}

/**
 * Rectangular metric card used by Team and Reports headers. Mirrors the
 * design's `.stat` element: tabular-num value, uppercase label, optional
 * tinted delta line.
 */
export function StatCard({ label, value, sub, hint, hintTone = 'neutral', className }: Props) {
  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-lg border border-border bg-card px-5 py-4',
        className,
      )}
    >
      <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {label}
      </div>
      <div className="text-2xl font-semibold tabular-nums">
        {value}
        {sub && <span className="ml-1 text-sm font-normal text-muted-foreground">{sub}</span>}
      </div>
      {hint && (
        <div
          className={cn(
            'text-xs',
            hintTone === 'up' && 'text-emerald-600',
            hintTone === 'down' && 'text-destructive',
            hintTone === 'neutral' && 'text-muted-foreground',
          )}
        >
          {hint}
        </div>
      )}
    </div>
  )
}
